import {
  VOICE_CONSENT_VERSION,
  type DraftVoiceDto,
  type Manner,
  type OwnTakeResult,
  type RecordingCheckResult,
  type RecordingKind,
  type StartStockVoiceoverInput,
  type StartVoiceoverInput,
  type StartVoiceoverResult,
  type VoiceChatStartResult,
  type VoiceCreateResult,
  type VoiceDeleteResult,
  type VoiceoverDto,
  type VoiceProfileDto,
} from '@app/shared';

/**
 * The Mini App's client of the voice routes (stage 4). Kept apart from
 * `api-core.ts`, which stage 2 grows, so the two stages never edit one file.
 */

export class VoiceApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = 'VoiceApiError';
  }
}

export interface VoiceApiDeps {
  baseUrl: string;
  getInitData: () => string;
  fetchImpl?: typeof fetch;
}

export interface VoiceApi {
  getVoiceProfile(): Promise<VoiceProfileDto>;
  checkRecording(kind: RecordingKind, audio: Blob): Promise<RecordingCheckResult>;
  createVoice(input: {
    sample: Blob;
    consent: Blob;
    consentAccepted: boolean;
  }): Promise<VoiceCreateResult>;
  getVoicePreview(): Promise<Blob>;
  confirmVoice(): Promise<VoiceProfileDto>;
  deleteVoice(): Promise<VoiceDeleteResult>;
  /** Spec 6.3: the bot waits for the sample and the consent in the chat. */
  startChatSample(): Promise<VoiceChatStartResult>;
  cancelChatSample(): Promise<{ ok: true }>;

  getDraftVoice(draftId: number): Promise<DraftVoiceDto>;
  startCloneVoiceover(draftId: number, body: StartVoiceoverInput): Promise<StartVoiceoverResult>;
  /** A take in a ready-made voice (spec 5.2). */
  startStockVoiceover(draftId: number, body: StartStockVoiceoverInput): Promise<StartVoiceoverResult>;
  /** The clone's sample phrase in a manner (spec 5.3); never a public link. */
  getCloneSample(manner: Manner): Promise<Blob>;
  uploadOwnTake(draftId: number, audio: Blob): Promise<OwnTakeResult>;
  getVoiceover(id: number): Promise<VoiceoverDto>;
  getVoiceoverAudio(id: number): Promise<Blob>;
}

/** `sample.webm`, `consent.m4a`…: the API keeps the original under this extension. */
export function voiceUploadName(stem: string, type: string): string {
  const ext = /mp4|m4a|aac/.test(type)
    ? 'm4a'
    : /ogg/.test(type)
      ? 'ogg'
      : /wav/.test(type)
        ? 'wav'
        : 'webm';
  return `${stem}.${ext}`;
}

export function createVoiceApi(deps: VoiceApiDeps): VoiceApi {
  const doFetch = deps.fetchImpl ?? fetch;

  /** JSON by default; FormData goes as multipart (the browser sets its boundary). */
  async function send(method: string, path: string, body?: unknown): Promise<Response> {
    const isForm = body instanceof FormData;
    let res: Response;
    try {
      res = await doFetch(`${deps.baseUrl}${path}`, {
        method,
        headers: isForm
          ? { 'X-Init-Data': deps.getInitData() }
          : { 'Content-Type': 'application/json', 'X-Init-Data': deps.getInitData() },
        body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
      });
    } catch (err) {
      throw new VoiceApiError(0, 'NETWORK', err instanceof Error ? err.message : 'Network error');
    }
    if (!res.ok) {
      let code = 'UNKNOWN';
      let message = `HTTP ${res.status}`;
      try {
        const data = (await res.json()) as { error?: { code?: string; message?: string } };
        code = data.error?.code ?? code;
        message = data.error?.message ?? message;
      } catch {
        // The body is not JSON (a proxy error page): keep the status line.
      }
      throw new VoiceApiError(res.status, code, message);
    }
    return res;
  }

  const json = async <T>(method: string, path: string, body?: unknown): Promise<T> =>
    (await (await send(method, path, body)).json()) as T;
  const blob = async (path: string): Promise<Blob> => (await send('GET', path)).blob();

  return {
    getVoiceProfile: () => json('GET', '/voice/profile'),
    checkRecording: (kind, audio) => {
      const form = new FormData();
      form.append('kind', kind);
      form.append('audio', audio, voiceUploadName(kind, audio.type));
      return json('POST', '/voice/check', form);
    },
    createVoice: ({ sample, consent, consentAccepted }) => {
      const form = new FormData();
      form.append('sample', sample, voiceUploadName('sample', sample.type));
      form.append('consent', consent, voiceUploadName('consent', consent.type));
      form.append('consentVersion', VOICE_CONSENT_VERSION);
      form.append('consentAccepted', String(consentAccepted));
      return json('POST', '/voice/profile', form);
    },
    getVoicePreview: () => blob('/voice/profile/preview'),
    confirmVoice: () => json('POST', '/voice/profile/confirm'),
    deleteVoice: () => json('DELETE', '/voice/profile'),
    startChatSample: () =>
      json('POST', '/voice/chat', { consentVersion: VOICE_CONSENT_VERSION, consentAccepted: true }),
    cancelChatSample: () => json('DELETE', '/voice/chat'),

    getDraftVoice: (draftId) => json('GET', `/voiceovers/drafts/${draftId}`),
    startCloneVoiceover: (draftId, body) =>
      json('POST', `/voiceovers/drafts/${draftId}/clone`, body),
    startStockVoiceover: (draftId, body) => json('POST', `/voiceovers/drafts/${draftId}/stock`, body),
    getCloneSample: (manner) => blob(`/voice/profile/samples/${manner}`),
    uploadOwnTake: (draftId, audio) => {
      const form = new FormData();
      form.append('audio', audio, voiceUploadName('take', audio.type));
      return json('POST', `/voiceovers/drafts/${draftId}/own`, form);
    },
    getVoiceover: (id) => json('GET', `/voiceovers/${id}`),
    getVoiceoverAudio: (id) => blob(`/voiceovers/${id}/audio`),
  };
}
